import { cord, formatInput, gql2 } from "@/gql/lib";
import {
  ActionEnum,
  AxisType,
  CountParametersTier1Tier2,
  Entity,
  getEmailAddressFromExtendedData,
  GroupByEnum,
  ItemFilter,
  SearchParameter,
  SearchResultEnum,
  SearchResultEnumHelper,
  SearchTargetType,
} from "@/gql/types";
import { ListResult, PopulationTypeHelper, POPULATION_TYPE } from "../models";
import { SharedState } from "../state";
import { FileModelHelper } from "@/gql/file-parser";

export const getEntityById = gql2<string, Entity>(
  "entity-by-id",
  (id: string) => {
    return {
      query: `
      {
        entity(id:"${id}"){
          id
          type
          name
          externalId
          isExternal
          data
          subType
        }
      }
      `,
      mapper: (res) => res.entity,
    };
  }
);

export const get_folder_by_id = gql2<string, Entity>(
  "entity-by-id",
  (id: string) => {
    return {
      query: `
      {
        entity(id:"${id}"){
          id
          type
          name
          externalId
          subType
          labels{
            id
            name
            concreteScore 
            confidence
            generation
            type
            labelType
            labelCategory
          }
          extendedData
          sourceApplication
          sourceApplicationSubType
        }
      }
      `,
      //    groupType
      mapper: (res) => res.entity,
    };
  },
  {
    cacheMaxTime: 0,
  }
);

export const getFolders = gql2("folders.get", (cursor: string, timeFrame) => {
  const q: CountParametersTier1Tier2 = {
    coordinate: {
      x: cord(AxisType.Folder, []),
      y: cord(AxisType.None, []),
      z: timeFrame.total,
    },
    cursor,
    action: ActionEnum.RecordCount,
    groupBy: GroupByEnum.XValue,
  };
  return {
    query: `
          {
            entities: tier2count(params:${formatInput(q)}){
              cursor
              entities{
                id
                name
                externalId
              }
            } 
          }
      `,
    mapper: (res) => {
      const { entities } = res;
      let cursor = "";
      if (entities.length > 0) {
        cursor = entities[0].cursor;
      }
      return {
        cursor,
        objects: entities.map((item: any) => ({
          ...item.entities[0],
          type: POPULATION_TYPE.FOLDER,
        })),
      };
    },
  };
});

export const getEmployees = gql2(
  "employees.get",
  (cursor: string, timeFrame) => {
    const q: CountParametersTier1Tier2 = {
      coordinate: {
        x: cord(AxisType.Employee, []),
        y: cord(AxisType.None, []),
        z: timeFrame.total,
      },
      cursor,
      action: ActionEnum.RecordCount,
      groupBy: GroupByEnum.XValue,
    };
    return {
      query: `
          {
            entities: tier2count(params:${formatInput(q)}){
              cursor
              entities{
                id
                name
                extendedData
              }
            } 
          }
      `,
      mapper: (res) => {
        const { entities } = res;
        let cursor = "";
        if (entities.length > 0) {
          cursor = entities[0].cursor;
        }
        return {
          cursor,
          objects: entities.map((item: any) => {
            const email = getEmailAddressFromExtendedData(item.entities[0]);
            return {
              ...item.entities[0],
              type: POPULATION_TYPE.EMPLOYEE,
              name:
                (item.entities[0].name ? item.entities[0].name + " - " : "") +
                email,
            };
          }),
        };
      },
    };
  }
);

export const getContacts = gql2("contacts.get", (cursor: string, timeFrame) => {
  const q: CountParametersTier1Tier2 = {
    coordinate: {
      x: cord(AxisType.ExternalReceiver, []),
      y: cord(AxisType.None, []),
      z: timeFrame.total,
    },
    cursor,
    action: ActionEnum.RecordCount,
    groupBy: GroupByEnum.XValue,
  };
  return {
    query: `
          {
            entities: tier2count(params:${formatInput(q)}){
              cursor
              entities{
                id
                name
                externalId
                extendedData
              }
            }
          }
      `,
    mapper: (res) => {
      const { entities } = res;
      const objects = entities.map((item: any) => {
        const email = getEmailAddressFromExtendedData(item.entities[0]);
        return {
          ...item.entities[0],
          type: POPULATION_TYPE.CONTACT,
          name: email,
        };
      });
      let cursor = "";
      if (entities.length > 0) {
        cursor = entities[0].cursor;
      }
      return {
        cursor,
        objects:
          cursor === ""
            ? [
                {
                  type: POPULATION_TYPE.CONTACT,
                  name: "Anonymous user",
                  id: SharedState.anonymousUserId.value,
                },
                ...objects,
              ]
            : objects,
      };
    },
  };
});

export const getOrganizations = gql2(
  "contacts.get",
  (cursor: string, timeFrame) => {
    const q: CountParametersTier1Tier2 = {
      coordinate: {
        x: cord(AxisType.OrganizationReceiver, []),
        y: cord(AxisType.None, []),
        z: timeFrame.total,
      },
      cursor,
      action: ActionEnum.RecordCount,
      groupBy: GroupByEnum.XValue,
    };
    return {
      query: `
          {
            entities: tier2count(params:${formatInput(q)}){
              cursor
              entities{
                id
                name
                externalId
              }
            }
          }
      `,
      mapper: (res) => {
        const { entities } = res;
        let cursor = "";
        if (entities.length > 0) {
          cursor = entities[0].cursor;
        }
        return {
          cursor,
          objects: entities.map((item: any) => ({
            ...item.entities[0],
            type: POPULATION_TYPE.ORGANIZATION,
          })),
        };
      },
    };
  }
);

export const getPersonalAddress = gql2(
  "personal-address.get",
  (cursor: string, timeFrame) => {
    const q: CountParametersTier1Tier2 = {
      coordinate: {
        x: cord(AxisType.PersonalAddress, []),
        y: cord(AxisType.None, []),
        z: timeFrame.total,
      },
      cursor,
      action: ActionEnum.RecordCount,
      groupBy: GroupByEnum.XValue,
    };
    return {
      query: `
          {
            entities: tier2count(params:${formatInput(q)}){
              cursor
              entities{
                id
                name
                externalId
                extendedData
              }
            }
          }
      `,
      mapper: (res) => {
        const { entities } = res;
        let cursor = "";
        if (entities.length > 0) {
          cursor = entities[0].cursor;
        }
        return {
          cursor,
          objects: entities.map((item: any) => {
            return {
              ...item.entities[0],
              name: getEmailAddressFromExtendedData(item.entities[0]),
              type: POPULATION_TYPE.PRIVATE_EMAIL,
            };
          }),
        };
      },
    };
  }
);

export const getItems = gql2("items.get", (cursor: string, timeFrame) => {
  const q: ItemFilter = {
    coordinates: [
      {
        //@ts-ignore
        x: cord(AxisType.None, ["none"]),
        //@ts-ignore
        y: cord(AxisType.None, ["none"]),
        z: timeFrame.total,
      },
    ],
    cursor,
  };
  return {
    query: `
        {
          items(filter:${formatInput(q)}){
            cursor
            id
            name
            externalId
         }
        }
      `,
    mapper: (res) => {
      const { items } = res;
      let cursor = "";
      if (items.length > 0) {
        cursor = items[0].cursor;
      }
      return {
        cursor,
        objects: items.map((item: any) => ({
          ...item,
          type: POPULATION_TYPE.FILE,
        })),
      };
    },
  };
});

//#region SEARCH
export const searchEntities = gql2<SearchParameter, ListResult<any>>(
  "entities.search",
  (params) => {
    if (params.searchQuery.length === 0) {
      params.searchQuery = "%20";
    }
    const personalAddressParams: SearchParameter = {
      ...params,
      targetType: SearchTargetType.PersonalAddresses,
    };
    return {
      query: `
        {
          search(filter:${formatInput(params)}){
            ... on ${SearchResultEnumHelper.fromSearchTargetType(
              params.targetType
            )}{
              cursor
              id
              ${
                [
                  SearchTargetType.Organizations,
                  SearchTargetType.Employees,
                  SearchTargetType.Folders,
                  SearchTargetType.Files,
                ].includes(params.targetType)
                  ? "name"
                  : ""
              }
              ${
                [SearchTargetType.Folders].includes(params.targetType)
                  ? "path"
                  : ""
              }
              ${
                params.targetType === SearchTargetType.Files
                  ? `clusterUID
                subType
                category
                `
                  : ""
              }
              
              ${
                [
                  SearchTargetType.OrganizationContacts,
                  SearchTargetType.ExternalContacts,
                  SearchTargetType.Employees,
                  SearchTargetType.PersonalAddresses,
                ].includes(params.targetType)
                  ? "email"
                  : ""
              }
           }
          }
      
          ${
            params.targetType === SearchTargetType.OrganizationContacts
              ? `   
            personal:   search(filter:${formatInput(personalAddressParams)}){
                ... on ${SearchResultEnum.PersonalAddressesSearchResult}{
                  cursor
                  id
                 email
                }
               }
            `
              : ""
          } 
         
        }
        `,
      mapper: (res) => {
        let { search = [], personal = [] } = res;
        search = search ? search : [];
        personal = personal ? personal : [];
        const all = [
          ...search.map((t: any) => {
            return {
              ...t,
              type: PopulationTypeHelper.fromSearchTargetType(
                params.targetType
              ),
            };
          }),
          ...personal.map((t: any) => {
            return {
              ...t,
              eventBusType: PopulationTypeHelper.fromSearchTargetType(
                params.targetType
              ),
              type: POPULATION_TYPE.PRIVATE_EMAIL,
            };
          }),
        ];
        let cursor = "";
        if (all.length > 0) {
          cursor = all[0].cursor;
        }
        if (params.targetType === SearchTargetType.Employees) {
          return {
            cursor,
            objects: all.map((item: any) => {
              return {
                ...item,
                name: (item.name ? item.name + " - " : "") + item.email,
                short_name: item.name || item.email,
              };
            }),
          };
        } else {
          return {
            cursor,
            objects: all
              .filter((item) => item.email || item.name)
              .map((item: any) => {
                const is_email_body = FileModelHelper.is_email_body(item);
                const name = item.email || item.name;
                return {
                  ...item,
                  name: is_email_body ? "Email Subject: " + name : name,
                  is_email_body: FileModelHelper.is_email_body(item),
                };
              }),
          };
        }
      },
    };
  }
);

export const getEventById = gql2("event.get", (id: string) => {
  return {
    query: `
         {
          event(id:"${id}"){
            cursor
            id
            subType
            justification{
              id
              status
            }
            labels{
              id
              name
              type
              labelType
              labelCategory
              concreteScore
            }
            date
            sourceApplication
            insights{
              id
              name
              description
              severity
            }
            items{
              id
              name
              subType
              category
              extendedProperties{
                topic
              }
              clusterUID
              path
              labels{
                infoLabel{
                  id
                  name
                }
                adhocLabels{
                  id
                  name
                }
                piis{
                  id
                  name
                }
                ctxLabels{
                  id
                  name
                }
              }
            }
            severity
            mark
            relatedEntities{
              fileUploadPath
              exposedBy{
                entity{
                  id
                  email
                  name
                  type
                  isExternal
                  department
                }
              }
              exposedTo{
                relationType
                entity{
                  id
                  isExternal
                  isPersonalAddress
                  name
                  type
                  email
                  department
                }
              }
            }
          }     
         }
        `,
    mapper: (res) => res.event,
  };
});

export const getItemById = gql2(
  "item.get",
  (id: string) => {
    return {
      query: `
         {
          item(id:"${id}"){
            id
            name
            subType
            category
            externalId
            clusterUID
            extendedProperties{
              topic
            }
            labelHistory{
              userId
            }
            labels{
              id
              name
              type
              labelType
              labelCategory
              abstracts{
                id
              }
              concreteScore 
              generation
              confidence
            }
            sourceApplication
            sensitivity
            createdAt
            extendedData
          }
         }
        `,
      mapper: (res) => res.item,
    };
  },
  { cacheMaxTime: 0 }
);
